import { useEffect, useState } from 'react';
import * as locCon from '../LocalConstants';

import { I18n } from 'i18n-js';

// Languages
import en from '../languages/en.json';
import es from '../languages/es.json';
import gu from '../languages/gu.json';
import hi from '../languages/hi.json';

import { StateStoredValue, getStoredValue, RefStoredValue } from './GeneralStoreHooks';

// Set the key-value pairs for the different languages you want to support.
const baseLanguages = {
  en: en,
  es: es,
  gu: gu,
  hi: hi,
};

const buildI18n = (locale) => {
  const newI18n = new I18n(baseLanguages);
  newI18n.locale = locale;
  // Falls back to english, so a key missing from a translation renders the
  // english text instead of the [missing "..."] placeholder.
  newI18n.enableFallback = true;

  return newI18n;
};

const baseI18n = buildI18n(locCon.DEFAULT_LOCALE);

/**
 * The locale is the one piece of state that must outlive a reload. The store is
 * deliberately not persisted (see APP_TOKEN in LocalConstants), so it is kept in
 * localStorage on its own instead of pulling redux-persist over the whole state.
 */
export const persistLocale = (locale) => {
  try {
    localStorage.setItem(locCon.LOCALE_STORAGE_KEY, locale);
  } catch (error) {
    // Private browsing or blocked storage. The locale simply will not survive
    // the reload, which is not worth failing the language change over.
    console.log(error);
  }
};

export const getPersistedLocale = () => {
  try {
    const stored = localStorage.getItem(locCon.LOCALE_STORAGE_KEY);
    return locCon.SUPPORTED_LOCALES.includes(stored) ? stored : locCon.DEFAULT_LOCALE;
  } catch (error) {
    console.log(error);
    return locCon.DEFAULT_LOCALE;
  }
};

export const RefLanguage = () => {
  const currentLocale = RefStoredValue(locCon.CURRENT_LOCALE);
  const [i18n, setI18n] = useState(baseI18n);

  // If locale changes
  useEffect(() => {
    setI18n(buildI18n(currentLocale));
  }, [currentLocale]);

  return i18n;
};

export const LanguageState = () => {
  const [currentLocale, setStoredLocale] = StateStoredValue(locCon.CURRENT_LOCALE);
  const [i18n, setI18n] = useState(baseI18n);

  // If locale changes
  useEffect(() => {
    setI18n(buildI18n(currentLocale));
  }, [currentLocale]);

  const setCurrentLocale = (locale) => {
    persistLocale(locale);
    setStoredLocale(locale);
  };

  return [i18n, currentLocale, setCurrentLocale];
};

export const RefLanguageAndLocale = () => {
  const currentLocale = RefStoredValue(locCon.CURRENT_LOCALE);
  const [i18n, setI18n] = useState(baseI18n);

  // If locale changes
  useEffect(() => {
    setI18n(buildI18n(currentLocale));
  }, [currentLocale]);

  return [i18n, currentLocale];
};

export const RefLocale = () => {
  const currentLocale = RefStoredValue(locCon.CURRENT_LOCALE);

  return currentLocale;
};

/**
 * Escape hatch for the code that produces user facing text outside of any
 * component (store actions, utils), where a hook cannot be called.
 */
export const getCurrentI18n = () => {
  return buildI18n(getStoredValue(locCon.CURRENT_LOCALE));
};

// A sentinel no catalogue value can collide with. i18n-js ignores a null
// defaultValue and hands back its [missing "..."] placeholder instead, so
// asking with null answers yes for every key. A string default is honoured,
// including through the english fallback.
const ABSENT = '__community_hats_absent__';

export const translationExists = (i18n, key) => {
  if (key === null || key === undefined || typeof key !== 'string' || key === '') return false;

  return i18n.t(key, { defaultValue: ABSENT }) !== ABSENT;
};
